import { apiClient } from '@/api/client';
import type {
  FaqEntry,
  FaqEntryUpsertRequest,
  ParentOpinion,
  ParentOpinionUpsertRequest,
} from '@/types/shared';

/**
 * Website content API client (T140) — typed to the frozen `packages/shared/src/contracts/
 * website-content.ts` contract (T136) against the live T139 `WebsiteContentModule` endpoints.
 * Every endpoint here is `@Roles('Admin')` server-side (mirrors `lookups.api.ts`'s Admin-only
 * writes) — consumed exclusively by `/settings/content` (`ContentSettingsPage` /
 * `ContentEntityManager`). `sortOrder` is NEVER part of a create/update body (server-managed,
 * FR-7) — the only way to change order is `reorderParentOpinions`/`reorderFaqEntries` below.
 */

export interface ContentListParams {
  /** Omit to fetch every row (active + inactive) — the Admin manager's default view (FR-13,
   * Assumption B). Pass `true` to narrow to active-only, mirroring `lookups.api.ts`. */
  activeOnly?: boolean;
}

function listParams(params?: ContentListParams) {
  return params?.activeOnly === undefined ? undefined : { activeOnly: params.activeOnly };
}

/* ---------------------------------------------------------------------------------------------
 * Parents' Opinions — /content/parent-opinions
 * ------------------------------------------------------------------------------------------- */

/** GET /content/parent-opinions — every row (active+inactive) by default, ordered by sortOrder. */
export function listParentOpinions(params?: ContentListParams): Promise<ParentOpinion[]> {
  return apiClient
    .get<ParentOpinion[]>('/content/parent-opinions', { params: listParams(params) })
    .then((res) => res.data);
}

/** POST /content/parent-opinions — appends at `sortOrder = max + 1` server-side. */
export function createParentOpinion(payload: ParentOpinionUpsertRequest): Promise<ParentOpinion> {
  return apiClient.post<ParentOpinion>('/content/parent-opinions', payload).then((res) => res.data);
}

/** PATCH /content/parent-opinions/{id} — content edit and/or activate/deactivate (FR-10). */
export function updateParentOpinion(
  id: number,
  payload: ParentOpinionUpsertRequest,
): Promise<ParentOpinion> {
  return apiClient
    .patch<ParentOpinion>(`/content/parent-opinions/${id}`, payload)
    .then((res) => res.data);
}

/** PATCH /content/parent-opinions/reorder — `ids` MUST be the entity's full id set (active +
 * inactive), in the new order; 400 `REORDER_IDS_MISMATCH` on a partial/duplicate/unknown list. */
export function reorderParentOpinions(ids: number[]): Promise<ParentOpinion[]> {
  return apiClient
    .patch<ParentOpinion[]>('/content/parent-opinions/reorder', { ids })
    .then((res) => res.data);
}

/** DELETE /content/parent-opinions/{id} — TRUE hard delete (FR-11); no restore endpoint exists. */
export function deleteParentOpinion(id: number): Promise<void> {
  return apiClient.delete(`/content/parent-opinions/${id}`).then(() => undefined);
}

/* ---------------------------------------------------------------------------------------------
 * FAQ entries — /content/faq
 * ------------------------------------------------------------------------------------------- */

/** GET /content/faq — every row (active+inactive) by default, ordered by sortOrder. */
export function listFaqEntries(params?: ContentListParams): Promise<FaqEntry[]> {
  return apiClient
    .get<FaqEntry[]>('/content/faq', { params: listParams(params) })
    .then((res) => res.data);
}

/** POST /content/faq — appends at `sortOrder = max + 1` server-side. */
export function createFaqEntry(payload: FaqEntryUpsertRequest): Promise<FaqEntry> {
  return apiClient.post<FaqEntry>('/content/faq', payload).then((res) => res.data);
}

/** PATCH /content/faq/{id} — content edit and/or activate/deactivate (FR-10). */
export function updateFaqEntry(id: number, payload: FaqEntryUpsertRequest): Promise<FaqEntry> {
  return apiClient.patch<FaqEntry>(`/content/faq/${id}`, payload).then((res) => res.data);
}

/** PATCH /content/faq/reorder — `ids` MUST be the entity's full id set (active + inactive), in
 * the new order; 400 `REORDER_IDS_MISMATCH` on a partial/duplicate/unknown list. */
export function reorderFaqEntries(ids: number[]): Promise<FaqEntry[]> {
  return apiClient.patch<FaqEntry[]>('/content/faq/reorder', { ids }).then((res) => res.data);
}

/** DELETE /content/faq/{id} — TRUE hard delete (FR-11); no restore endpoint exists. */
export function deleteFaqEntry(id: number): Promise<void> {
  return apiClient.delete(`/content/faq/${id}`).then(() => undefined);
}
